import type { LivePayload } from '../../shared/live';

export interface LiveTransportCursor {
  sequence: number | null;
  revision: number | null;
}

export interface LiveFrameDecision {
  accepted: boolean;
  duplicate: boolean;
  sequenceGap: boolean;
  revisionGap: boolean;
  /** The active EventSource should be replaced by a fresh snapshot. */
  resetRequired: boolean;
  next: LiveTransportCursor;
}

/**
 * Validate one platform SSE frame without knowing anything about its source.
 * Old servers may omit metadata; those frames remain fully usable and simply
 * skip gap detection.
 */
export function inspectLiveFrame(previous: LiveTransportCursor, payload: LivePayload): LiveFrameDecision {
  const sequence = Number.isSafeInteger(payload.sequence) ? payload.sequence! : null;
  const revision = Number.isSafeInteger(payload.revision) ? payload.revision! : null;
  if (sequence === null && revision === null) return { accepted: true, duplicate: false, sequenceGap: false, revisionGap: false, resetRequired: false, next: previous };

  const oldSequence = previous.sequence;
  const oldRevision = previous.revision;
  const sequenceGap = sequence !== null && oldSequence !== null && sequence > oldSequence + 1;
  // A status checkpoint cannot supply a missing fact. Even a one-revision
  // jump on a heartbeat means the client must fetch a recording snapshot.
  const revisionGap = revision !== null && oldRevision !== null && revision > oldRevision + (payload.type === 'status' ? 0 : 1);
  const older = sequence !== null && oldSequence !== null && sequence < oldSequence;
  const duplicate = sequence !== null && oldSequence !== null && sequence === oldSequence && payload.type === 'events';
  const snapshot = payload.type === 'recording';
  const resetRequired = !snapshot && (sequenceGap || revisionGap || older);
  const accepted = !duplicate && (!older || snapshot) && (!sequenceGap && !revisionGap || snapshot);
  return {
    accepted,
    duplicate,
    sequenceGap,
    revisionGap,
    resetRequired,
    next: {
      sequence: sequence === null ? oldSequence : snapshot ? sequence : Math.max(oldSequence ?? sequence, sequence),
      revision: revision === null ? oldRevision : snapshot ? revision : Math.max(oldRevision ?? revision, revision),
    },
  };
}
